import React, { useState } from 'react';
import ExpenseList from '../components/ExpenseList.tsx';
import ExpenseForm from '../components/ExpenseForm.tsx';
import ExpenseCategoryManager from '../components/ExpenseCategoryManager.tsx';
import { Plus, Download, Tags } from 'lucide-react';
import { useExpenseStats, useExportExpenses } from '../hooks/useExpenses.ts';
import ExportFormatModal from '../components/ExportFormatModal.tsx';
import { PermissionGate, PermissionPage } from '../components/PermissionGate';
import { usePermissions } from '../hooks/usePermissions';
import { PERMISSIONS } from '../utils/permissions';

const ExpensesPage = () => {
  const { can } = usePermissions();
  const [showForm, setShowForm] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [filters, setFilters] = useState({ search: '', category: '', dateRange: [null, null] });
  const [showExportModal, setShowExportModal] = useState(false);
  const [showCategoryManager, setShowCategoryManager] = useState(false);

  const { data: stats, isLoading: statsLoading, error: statsError, refetch: refetchStats } = useExpenseStats();
  const exportExpenses = useExportExpenses();

  const handleAddExpense = () => {
    setEditingExpense(null);
    setShowForm(true);
  };

  const handleEditExpense = (expense) => {
    setEditingExpense(expense);
    setShowForm(true);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingExpense(null);
  };

  const handleExport = (format) => {
    exportExpenses.mutate({ filters, format });
  };

  return (
    <PermissionPage permission={PERMISSIONS.VIEW_EXPENSES}>
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Expenses</h1>
              <p className="mt-2 text-gray-600">Track and manage your group expenses</p>
            </div>
            <div className="mt-4 sm:mt-0 flex flex-wrap gap-2">
              <PermissionGate permission={PERMISSIONS.VIEW_EXPENSES}>
              <button
                onClick={() => setShowExportModal(true)}
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
              >
                <Download className="w-4 h-4 mr-2 inline" /> Export
              </button>
              </PermissionGate>
              <PermissionGate permission={PERMISSIONS.VIEW_EXPENSES}>
              <button
                type="button"
                onClick={() => setShowCategoryManager(true)}
                className="bg-white border border-gray-300 hover:bg-gray-50 text-gray-800 px-4 py-2 rounded-lg font-medium transition-colors"
              >
                <Tags className="w-4 h-4 mr-2 inline" /> Categories
              </button>
              </PermissionGate>
              <PermissionGate permission={PERMISSIONS.CREATE_EXPENSE}>
              <button
                onClick={handleAddExpense}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-medium transition-colors"
              >
                <Plus className="w-4 h-4 mr-2 inline" /> Add Expense
              </button>
              </PermissionGate>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8 kpi-grid">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-sm font-medium text-gray-600">Total Expenses</div>
            <div className="text-2xl font-bold text-gray-900">
              {statsLoading ? '--' : statsError ? (
                <span>
                  Something went wrong. <button onClick={refetchStats} className="ml-2 px-2 py-1 bg-blue-100 text-blue-700 rounded">Retry</button>
                </span>
              ) : `KSh ${Number(stats?.total_expenses || 0).toLocaleString()}`}
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-sm font-medium text-gray-600">Categories</div>
            <div className="text-2xl font-bold text-gray-900">
              {statsLoading ? '--' : statsError ? (
                <span>
                  Something went wrong. <button onClick={refetchStats} className="ml-2 px-2 py-1 bg-blue-100 text-blue-700 rounded">Retry</button>
                </span>
              ) : Object.keys(stats?.categories || {}).length}
            </div>
          </div>
        </div>

        {/* Filters (search only) */}
        <div className="bg-white rounded-lg shadow mb-6 p-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <input
                type="text"
                placeholder="Search expenses..."
                value={filters.search}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>

        {/* Expenses Table */}
        <div className="bg-white rounded-lg shadow">
          <ExpenseList onEdit={handleEditExpense} filters={filters} />
        </div>

        {/* Modal for Add/Edit Expense */}
        {showForm && (
          <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-lg">
              <ExpenseForm
                expense={editingExpense}
                onClose={handleCloseForm}
                onManageCategories={() => setShowCategoryManager(true)}
              />
            </div>
          </div>
        )}

        {showCategoryManager && (
          <ExpenseCategoryManager onClose={() => setShowCategoryManager(false)} />
        )}

        {/* Export Format Modal */}
        <ExportFormatModal
          isOpen={showExportModal}
          onClose={() => setShowExportModal(false)}
          onExport={handleExport}
          title="Export Expenses"
          subtitle="Choose the format for your expenses export:"
        />
      </div>
    </div>
    </PermissionPage>
  );
};

export default ExpensesPage; 